import cssMobile from '../../../css/editorMobile.module.css'
import {ImgMasked} from '../../../models/editor/imgMasked'
import {useAppDispatch, useAppSelector} from '../../../stores/configureStore'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../../../utils/helpers/origo'
import {
  addImageHistoryItem,
  addImageSelected,
  setAttribute,
} from '../../../stores/editor/editor.reducer'
import {IMG_ADDED} from '../../../models/editor/imgHistoryItemAction'
import {useEffect, useState} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faPlus, faTimes} from '@fortawesome/free-solid-svg-icons'
import {
  selectToolboxTabPanel,
  setPixaBayKeyword,
} from '../../../stores/toolbox/toolbox.reducer'
import {PixaBayImage} from '../../../models/toolbox/pixaBayImage'
import {Img} from '../../../models/editor/img'
import AwesomeDebouncePromise from 'awesome-debounce-promise'
import services from '../../../utils/services'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

interface AddPhotoModalProps {
  closed: boolean
  closeModal: () => void
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>
}

export default function AddPhotoModal({
  closed,
  closeModal,
  setIsUploading,
}: AddPhotoModalProps) {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {pixaBayKeyword, selectForMasked} = useAppSelector(
    store => store.toolbox,
  )
  const {printArea} = useAppSelector(store => store.editor)
  const [images, setImages] = useState<PixaBayImage[]>([])
  const selectForImage = useAppSelector(store =>
    store.editor.images.find(img => img.id === selectForMasked?.id),
  ) as ImgMasked | undefined

  const [, setLoading] = useState(false)

  const trySearchImages = async (keyword: string) => {
    try {
      const response = await services.searchImages(keyword)
      const responseData = response.data
      setImages(() =>
        responseData.hits.map(item => ({
          id: item.id,
          smallImageUrl: item.webformatURL,
          largeImageUrl: item.largeImageURL,
          tags: item.tags,
        })),
      )
    } catch (error) {
      setImages(() => [])
    }
  }
  const [debouncedSearch] = useState(() =>
    AwesomeDebouncePromise(trySearchImages, 1000),
  )

  useEffect(() => {
    ;(async () => {
      setLoading(() => true)
      await debouncedSearch(pixaBayKeyword)
      setLoading(() => false)
    })()
  }, [debouncedSearch, pixaBayKeyword])

  const uploadImage = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return
    }
    const file = files[0]
    try {
      setIsUploading(() => true)
      const response = await services.uploadImage(file)
      const imageUrl = response.data.url
      if (selectForMasked?.id && selectForImage) {
        const newInside = [...selectForImage.inside]
        newInside[selectForMasked.insideIndex] = {
          ...newInside[selectForMasked.insideIndex],
          url: imageUrl,
          position: origo,
          scale: 1,
        }
        dispatch(
          setAttribute({id: selectForMasked.id, data: {inside: newInside}}),
        )
        dispatch(
          selectToolboxTabPanel({
            tabPanel: 'Edit',
            selectForMasked: selectForMasked,
          }),
        )
      } else {
        const image: Img = {
          id: uuidv4(),
          type: 'url',
          url: imageUrl,
          position: printArea?.position ?? origo,
          inert: false,
          locked: false,
          brightness: 1,
          mirroredX: false,
          mirroredY: false,
          rotation: 0,
          scale: 1,
          filters: {
            inverse: false,
            monochrome: false,
            sepia: false,
          },
        }
        dispatch(addImageSelected({image}))
        dispatch(addImageHistoryItem({item: {img: image, action: IMG_ADDED}}))
      }
    } catch (e) {
      const error = e as AxiosError
      if (error.response?.status === 400) {
        alert(error.response.data)
      }
    }
    setIsUploading(() => false)
  }

  const selectImage = async (item: PixaBayImage) => {
    if (selectForMasked && selectForImage) {
      const newInside = [...selectForImage.inside]
      newInside[selectForMasked.insideIndex] = {
        ...newInside[selectForMasked.insideIndex],
        url: item.largeImageUrl,
        position: origo,
        scale: 1,
      }
      dispatch(
        setAttribute({
          id: selectForMasked.id,
          data: {inside: newInside},
        }),
      )
      dispatch(
        selectToolboxTabPanel({
          tabPanel: 'Edit',
          selectForMasked: selectForMasked,
        }),
      )
    } else {
      const newImg: Img = {
        id: uuidv4(),
        type: 'url',
        url: item.largeImageUrl,
        position: printArea?.position ?? origo,
        scale: 1,
        rotation: 0,
        brightness: 1,
        mirroredX: false,
        mirroredY: false,
        inert: false,
        locked: false,
        filters: {
          inverse: false,
          monochrome: false,
          sepia: false,
        },
      }
      dispatch(addImageSelected({image: newImg}))
      dispatch(addImageHistoryItem({item: {img: newImg, action: IMG_ADDED}}))
    }
    dispatch(setPixaBayKeyword({keyword: ''}))
    closeModal()
  }

  return (
    <div className={`${cssMobile.modal} ${closed ? cssMobile.closed : ''}`}>
      <div className={cssMobile.modalHeader}>
        <input
          className={cssMobile.input}
          type="text"
          value={pixaBayKeyword}
          placeholder={t('Keresés...')}
          onChange={e => {
            dispatch(setPixaBayKeyword({keyword: e.target.value}))
          }}></input>
        <FontAwesomeIcon
          icon={faTimes}
          size="2x"
          onClick={() => {
            dispatch(setPixaBayKeyword({keyword: ''}))
            closeModal()
          }}
        />
      </div>
      <div className={cssMobile.scrollColumns}>
        <div className={cssMobile.fileUploadCircleContainer}>
          <label
            htmlFor="mobilePhotoUpload"
            className={cssMobile.fileUploadCircle}>
            <FontAwesomeIcon
              icon={faPlus}
              size="2x"
              onClick={() => {
                dispatch(setPixaBayKeyword({keyword: ''}))
                closeModal()
              }}
            />
          </label>
          <input
            type="file"
            accept="image/*"
            id="mobilePhotoUpload"
            onChange={e => {
              uploadImage(e.target.files)
            }}
            hidden
          />
        </div>
        {images.map(item => (
          <div key={`pixabayImageMobile_${item.id}`}>
            <img
              src={item.smallImageUrl}
              alt={item.tags}
              title={item.tags}
              onClick={() => {
                selectImage(item)
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
